/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * ConnectLeads -- R14 Early Access spam containment.
 *
 * Admin surface listing website-sourced leads with a prominent
 * "Suspected spam" filter + one-click Not-spam clear. Lives at
 * /admin/connect-leads under the existing AdminLayout + admin auth.
 */

import React, { useCallback, useEffect, useState } from 'react';
import AdminLayout from '../../components/AdminLayout';
import adminApi from '../../services/adminApi';

const SKD_CYAN_DARK = '#0e7490';

const STATUSES = [
  { v: '',               label: 'All (website)' },
  { v: 'suspected_spam', label: 'Suspected spam' },
  { v: 'captured',       label: 'Captured' },
  { v: 'to_complete',    label: 'To complete' },
  { v: 'messaged',       label: 'Messaged' },
  { v: 'approved',       label: 'Approved' },
  { v: 'rejected',       label: 'Rejected' },
  { v: 'archived',       label: 'Archived' }
];

export default function ConnectLeads() {
  const [status, setStatus] = useState('suspected_spam');
  const [state, setState] = useState({ loading: true, leads: [], counts: {} });
  const [err, setErr] = useState(null);
  const [busyIds, setBusyIds] = useState(new Set());
  const [recheckBusy, setRecheckBusy] = useState(false);
  const [recheckResult, setRecheckResult] = useState(null);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    setErr(null);
    try {
      const qs = status ? `?status=${encodeURIComponent(status)}&limit=200` : '?limit=200';
      const r = await adminApi.request(`/admin/connect/leads${qs}`);
      const data = (r && r.data) || r;
      setState({ loading: false, leads: data.leads || [], counts: data.counts || {} });
    } catch (e) {
      setErr(e.message || 'Could not load leads.');
      setState({ loading: false, leads: [], counts: {} });
    }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  // Shared action runner. `op` is the trailing path segment on
  // /api/admin/connect/leads/:id/<op>. `confirmText` (optional) gates
  // the destructive ones (reject / archive) behind a window.confirm.
  const runAction = useCallback(async (id, op, confirmText) => {
    if (confirmText && typeof window !== 'undefined' && !window.confirm(confirmText)) return;
    setBusyIds((prev) => new Set([...prev, id]));
    setErr(null);
    try {
      await adminApi.request(
        `/admin/connect/leads/${encodeURIComponent(id)}/${op}`,
        { method: 'POST' }
      );
      await load();
    } catch (e) {
      setErr(e.message || `${op} failed.`);
    } finally {
      setBusyIds((prev) => { const n = new Set(prev); n.delete(id); return n; });
    }
  }, [load]);

  const runRecheck = useCallback(async () => {
    if (typeof window !== 'undefined' &&
        !window.confirm('Re-run the spam detector over existing captured leads? ' +
                        'Any lead that now triggers a signal will flip to suspected_spam.')) return;
    setRecheckBusy(true); setErr(null); setRecheckResult(null);
    try {
      const r = await adminApi.request('/admin/connect/leads/re-check', { method: 'POST' });
      const data = (r && r.data) || r;
      setRecheckResult(data);
      await load();
    } catch (e) {
      setErr(e.message || 'Re-check failed.');
    } finally {
      setRecheckBusy(false);
    }
  }, [load]);

  return (
    <AdminLayout>
      <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 280 }}>
            <h1 style={{ margin: '0 0 6px', color: '#0f172a' }}>Early Access leads</h1>
            <p style={{ color: '#64748b', fontSize: 14, marginTop: 0 }}>
              Rows from the public <code>/api/public/early-access</code> endpoint.
              "Suspected spam" rows are kept but hidden from the owner alert; clear
              or re-mark with one click.
            </p>
          </div>
          <button
            type="button"
            onClick={runRecheck}
            disabled={recheckBusy}
            title="Re-run the spam detector over existing captured leads. Any that trigger a signal flip to suspected_spam."
            style={{
              padding: '8px 14px', borderRadius: 8,
              background: 'white', color: SKD_CYAN_DARK,
              border: `1px solid ${SKD_CYAN_DARK}`,
              fontSize: 13, fontWeight: 600,
              cursor: recheckBusy ? 'not-allowed' : 'pointer',
              whiteSpace: 'nowrap'
            }}
          >
            {recheckBusy ? 'Re-checking…' : 'Re-check leads'}
          </button>
        </div>

        {recheckResult && (
          <div style={{
            padding: 10, background: '#f0fdf4', color: '#166534',
            borderRadius: 8, border: '1px solid #bbf7d0', fontSize: 13, margin: '10px 0'
          }}>
            Re-check scanned {recheckResult.scanned} captured lead{recheckResult.scanned === 1 ? '' : 's'};
            flagged {recheckResult.flagged} as suspected_spam.
            {recheckResult.by_signal && Object.entries(recheckResult.by_signal).filter(([, n]) => n > 0).length > 0 && (
              <span>
                {' '}Signals:{' '}
                {Object.entries(recheckResult.by_signal)
                  .filter(([, n]) => n > 0)
                  .map(([k, n]) => `${k}=${n}`)
                  .join(', ')}
                .
              </span>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '16px 0' }}>
          {STATUSES.map((s) => {
            const n = state.counts[s.v] || 0;
            const active = status === s.v;
            return (
              <button
                key={s.v || 'all'}
                type="button"
                onClick={() => setStatus(s.v)}
                style={{
                  padding: '6px 12px', borderRadius: 999,
                  background: active ? SKD_CYAN_DARK : 'white',
                  color: active ? 'white' : '#334155',
                  border: `1px solid ${active ? SKD_CYAN_DARK : '#cbd5e1'}`,
                  fontSize: 13, cursor: 'pointer'
                }}
              >
                {s.label}
                {s.v !== '' && (
                  <span style={{
                    marginLeft: 6,
                    fontSize: 11, fontWeight: 700,
                    padding: '1px 6px', borderRadius: 999,
                    background: active ? 'rgba(255,255,255,0.2)' : '#f1f5f9',
                    color: active ? 'white' : '#64748b'
                  }}>{n}</span>
                )}
              </button>
            );
          })}
        </div>

        {err && (
          <div style={{
            padding: 10, background: '#fef2f2', color: '#991b1b',
            borderRadius: 8, border: '1px solid #fecaca', marginBottom: 10, fontSize: 13
          }}>{err}</div>
        )}

        {state.loading && <div style={{ color: '#94a3b8' }}>Loading…</div>}
        {!state.loading && state.leads.length === 0 && (
          <div style={{
            padding: 16, background: 'white', borderRadius: 10,
            border: '1px dashed #cbd5e1', color: '#64748b', fontSize: 14
          }}>
            No leads in this view.
          </div>
        )}

        {!state.loading && state.leads.length > 0 && (
          <div style={{ background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                  <th style={styles.th}>Name</th>
                  <th style={styles.th}>Email</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Signals</th>
                  <th style={styles.th}>When</th>
                  <th style={styles.th}>Action</th>
                </tr>
              </thead>
              <tbody>
                {state.leads.map((l) => (
                  <tr key={l.id} style={{ borderTop: '1px solid #e2e8f0' }}>
                    <td style={styles.td}>
                      <div style={{ color: '#0f172a', fontWeight: 500 }}>{l.full_name || <em style={{ color: '#94a3b8' }}>(none)</em>}</div>
                      <div style={{ color: '#94a3b8', fontSize: 11 }}>{[l.represents, l.domain].filter(Boolean).join(' · ')}</div>
                    </td>
                    <td style={styles.td}>
                      <code style={{ fontSize: 12 }}>{l.email || <em>(none)</em>}</code>
                    </td>
                    <td style={styles.td}>
                      <StatusPill status={l.status} />
                    </td>
                    <td style={styles.td}>
                      {(l.spam_signals || []).length === 0 ? <span style={{ color: '#94a3b8' }}>-</span> : (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {l.spam_signals.map((s) => (
                            <span key={s} style={{
                              fontSize: 10, padding: '1px 6px', borderRadius: 999,
                              background: '#fef3c7', color: '#92400e'
                            }}>{s}</span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td style={styles.td}>
                      <span style={{ fontSize: 12, color: '#64748b' }}>
                        {new Date(l.created_at).toLocaleString()}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <RowActions
                        lead={l}
                        busy={busyIds.has(l.id)}
                        onNotSpam={() => runAction(l.id, 'not-spam')}
                        onMarkSpam={() => runAction(l.id, 'mark-spam')}
                        onReject={() => runAction(l.id, 'reject',
                          'Reject this lead? It stays in the DB but is marked rejected.')}
                        onArchive={() => runAction(l.id, 'archive',
                          'Archive this lead? It stays in the DB but moves to archived.')}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

// Per-row action buttons. Visibility rules keep the clutter down:
//  - 'Not spam' only on suspected_spam rows.
//  - 'Mark spam' hidden on suspected_spam rows.
//  - 'Reject' hidden on rejected/archived.
//  - 'Archive' hidden on archived.
function RowActions({ lead, busy, onNotSpam, onMarkSpam, onReject, onArchive }) {
  const s = lead.status;
  const btn = (label, onClick, variant = 'ghost') => (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      style={{
        padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600,
        cursor: busy ? 'not-allowed' : 'pointer',
        border: '1px solid #cbd5e1',
        background: variant === 'primary' ? '#0891b2' : 'white',
        color: variant === 'primary' ? 'white'
             : variant === 'danger'  ? '#991b1b' : '#334155',
        whiteSpace: 'nowrap'
      }}
    >
      {label}
    </button>
  );
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {s === 'suspected_spam' && btn(busy ? 'Clearing…' : 'Not spam', onNotSpam, 'primary')}
      {s !== 'suspected_spam' && btn('Mark spam', onMarkSpam)}
      {s !== 'rejected' && s !== 'archived' && btn('Reject', onReject, 'danger')}
      {s !== 'archived' && btn('Archive', onArchive)}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    suspected_spam: { bg: '#fef3c7', fg: '#92400e' },
    captured:       { bg: '#dcfce7', fg: '#166534' },
    to_complete:    { bg: '#dbeafe', fg: '#1e40af' },
    approved:       { bg: '#e0f2fe', fg: '#0369a1' },
    rejected:       { bg: '#fee2e2', fg: '#991b1b' },
    archived:       { bg: '#e5e7eb', fg: '#374151' }
  };
  const c = map[status] || { bg: '#f1f5f9', fg: '#334155' };
  return (
    <span style={{
      fontSize: 11, padding: '2px 8px', borderRadius: 999,
      background: c.bg, color: c.fg, whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.03em'
    }}>{status}</span>
  );
}

const styles = {
  th: { padding: '10px 12px', fontSize: 12, color: '#64748b', fontWeight: 600, borderBottom: '1px solid #e2e8f0' },
  td: { padding: '10px 12px', fontSize: 14, verticalAlign: 'top' }
};
